import { useCallback } from 'react';
import { useApiClient } from '@hooks/useApiClient';
import errorConfig from '@config/error.json';

/**
 * Custom hook for site-related data operations
 * Handles site details, configuration, and distribution data
 */
export const useSiteData = () => {
  const { get, post } = useApiClient();
  // Load all initial site data
  const loadSiteData = useCallback(async () => {
    try {
      // 1. Check if site is active first
      const siteActive = await get('/site/approved');

      // 2. Load site data
      const siteData = await get('/site');
      const totalMinted = await get('/fundraisin/totalminted');

      // 3. Load site distribution using POST method with site details
      const sliders = await post('/site/distribution', siteData);

      // 4. Load filter data based on site details
      const filterData = await get(
        `/artCreationPack/${siteData.artCreationPackId}/getfilterData`
      );

      // 7. Combine all site-related data
      const completeSiteDetails = {
        ...siteData,
        sliders: sliders || [],
        ...totalMinted,
        filterData
      };

      // 8. Check site activation and handle errors
      let error = null;
      if (!siteActive[0]) {
        if (siteActive[1] !== "SNA") {
          const errorMsg = errorConfig[siteActive[1]];
          if (errorMsg !== undefined) {
            error = errorMsg;
          }
        }
      }

      return {
        siteDetails: completeSiteDetails,
        error,
      };
    } catch (error) {
      console.error('Failed to load site data:', error);
      const errorMsg = errorConfig.UNKNOWN_ERROR || 'An unknown error occurred';
      throw new Error(errorMsg);
    }
  }, [get, post]);

  // Update total minted data
  const updateTotalMinted = useCallback(async () => {
    try {
      const totalMinted = await get('/fundraisin/totalminted');
      return totalMinted;
    } catch (error) {
      console.error('Failed to update total minted:', error);
      throw error;
    }
  }, [get]);

  return {
    loadSiteData,
    updateTotalMinted,
  };
};
